import React, { useState, useEffect } from 'react';
import type { BulkEmailDTO } from '../../services/apiClient';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import useCommunicationLists from '../../hooks/useCommunicationLists';
import handleChange from '../../utils/formUtils/handleChange';
import FileUpload from '../common/FileUpload';
import Button from '../../components/common/Button';

interface BulkEmailFormProps {
  initialValues: BulkEmailDTO;
  isSubmitting: boolean;
  setInitialValues: (values: BulkEmailDTO) => void;
  setAttachmentFile: (file: File | null) => void;
  onSubmit: (data: BulkEmailDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  list: {
    id: 'communication-list',
    title: 'Communication List',
  },
  content: {
    id: 'email-content',
    title: 'Email Content',
  },
  attachment: {
    id: 'attachment',
    title: 'Attachment',
  },
} as const;

const BulkEmailForm: React.FC<BulkEmailFormProps> = ({
  initialValues,
  isSubmitting,
  setInitialValues,
  setAttachmentFile,
  onSubmit,
  onCancel,
}) => {
  const { communicationLists, isLoading, error } = useCommunicationLists();
  const [replacementValues, setReplacementValues] = useState<
    Record<string, string>
  >({});
  const [formData, setFormData] = useState(initialValues);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files && e.target.files.length > 0) {
      setAttachmentFile(e.target.files[0]);
    }
  };

  const handleDeleteAttachment = (): void => {
    setInitialValues({ ...formData, attachmentPath: '' });
    setAttachmentFile(null); // Clear the file input
  };

  const updateReplacementValues = (
    communicationListId: string | undefined,
  ): void => {
    const selectedCommunicationList = communicationLists.find(
      (list) => list.id === communicationListId,
    );

    if (selectedCommunicationList?.metadata) {
      try {
        const metadata = JSON.parse(selectedCommunicationList.metadata);
        const values = metadata?.ReplacementValues || {};
        setReplacementValues(values);
      } catch (error) {
        console.error('Failed to parse metadata:', error);
        setReplacementValues({});
      }
    } else {
      setReplacementValues({});
    }
  };

  useEffect(() => {
    updateReplacementValues(formData.communicationListId);
  }, [formData.communicationListId, communicationLists]);

  useEffect(() => {
    if (!formData.communicationListId && communicationLists.length > 0) {
      const defaultCommunicationListId = communicationLists[0]?.id;
      setInitialValues({
        ...formData,
        communicationListId: defaultCommunicationListId,
      });
      updateReplacementValues(defaultCommunicationListId);
    }
  }, [communicationLists]);

  const handleSave = () => {
    onSubmit(formData);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
    >
      <FormSection
        id={FORM_SECTIONS.list.id}
        title={FORM_SECTIONS.list.title}
        columns={2}
      >
        {isLoading ? (
          <p>Loading communication lists...</p>
        ) : error ? (
          <p>Error loading communication lists: {error}</p>
        ) : (
          <FormField
            id="communicationListId"
            label="Communication List"
            type="dropdown"
            value={formData.communicationListId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            options={communicationLists.map((list) => ({
              value: list.id,
              label: list.name,
            }))}
            placeholder="Select a communication list"
          />
        )}
        {replacementValues && Object.keys(replacementValues).length > 0 && (
          <div className="mb-4 p-4 bg-gray-100 dark:bg-graphite rounded shadow">
            <h3 className="text-sm font-semibold text-gray-600 dark:text-cream mb-2">
              Available Replacement Values:
            </h3>
            <ul className="list-disc list-inside text-sm text-gray-700 dark:text-harbor">
              {Object.entries(replacementValues).map(([placeholder]) => (
                <li key={placeholder}>
                  <strong className="text-gray-800 dark:text-cream">
                    {placeholder}
                  </strong>
                </li>
              ))}
            </ul>
          </div>
        )}
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.content.id}
        title={FORM_SECTIONS.content.title}
        columns={2}
      >
        <FormField
          id="subject"
          label="Subject"
          type="text"
          value={formData.subject || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter email subject"
        />
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter title"
        />
        <FormField
          id="content"
          label="Content"
          type="richtext"
          value={formData.content || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter email content"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.attachment.id}
        title={FORM_SECTIONS.attachment.title}
        columns={1}
      >
        <div className="mb-4">
          <label
            htmlFor="attachmentPath"
            className="block text-sm font-medium text-gray-700"
          >
            Attachment
          </label>
          {formData.attachmentPath ? (
            <div className="flex items-center space-x-4 mt-2">
              <span className="text-sm text-gray-600">
                {formData.attachmentPath}
              </span>
              <Button
                variant="danger"
                onClick={handleDeleteAttachment}
                className="text-sm"
              >
                Remove
              </Button>
            </div>
          ) : (
            <FileUpload
              id="attachmentPath"
              title="Upload or drag and drop the attachment."
              accept={[
                '.pdf',
                '.docx',
                '.xlsx',
                '.csv',
                '.txt',
                '.png',
                '.jpg',
                '.jpeg',
                '.gif',
              ]}
              onChange={handleFileChange}
            />
          )}
        </div>
      </FormSection>
    </Form>
  );
};

export default BulkEmailForm;
